import React from 'react';

import { Card } from '~/components/core/Cards/Card';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import LabeledValue from '~/components/core/Molecules/LabledValue';
import Text from '~/components/core/TextComponents/Text';

const NOTIFICATIONS_WITH_CONDITIONS: string[] = [
  'CLAIM_OWNER_CHANGE_GENERIC',
  'FINANCIAL_REQUEST_DECISION_GENERIC',
  'FINANCIAL_REQUEST_GENERIC',
];

interface NotificationsConfigConditionsCard {
  notificationKey: string;
}

export const NotificationsConfigConditionsCard: React.FC<NotificationsConfigConditionsCard> = ({ notificationKey }) => {
  // Only show conditions for CLAIM_OWNER_CHANGE_GENERIC
  if (!NOTIFICATIONS_WITH_CONDITIONS.includes(notificationKey)) {
    return null;
  }

  return (
    <Card title="Conditions">
      <div className="grid grid-cols-1 gap-12">
        {notificationKey === 'CLAIM_OWNER_CHANGE_GENERIC' ? (
          <React.Fragment>
            <div className="flex flex-col space-y-2">
              <LabeledValue label="Self-Assignment Behavior" value="" />
              <div className="gap-x-3 grid grid-cols-[auto_1fr] items-center">
                <SwitchFormik id="predefined_notification_conditions.suppress_self_assignment" label="" hideErrorGap />
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM}>
                  Suppress self-assignment notifications
                </Text>
                <div />
                <Text variant={Text.VARIANTS.XS} className="text-gray-600">
                  When enabled, users will not receive notifications when they assign claims or exposures to themselves.
                </Text>
              </div>
            </div>
            <div className="flex flex-col space-y-2">
              <LabeledValue label="FNOL Ownership Behavior" value="" />
              <div className="gap-x-3 grid grid-cols-[auto_1fr] items-center">
                <SwitchFormik
                  id="predefined_notification_conditions.consolidate_fnol_ownership"
                  label=""
                  hideErrorGap
                />
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM}>
                  Consolidate claim &amp; exposure ownership during FNOL
                </Text>
                <div />
                <Text variant={Text.VARIANTS.XS} className="text-gray-600">
                  When enabled, an adjuster assigned during claim creation receives a single notification listing the
                  claim and/or exposures they were assigned, instead of one per assignment.
                </Text>
              </div>
            </div>
          </React.Fragment>
        ) : null}

        {notificationKey === 'FINANCIAL_REQUEST_DECISION_GENERIC' ? (
          <div className="flex flex-col space-y-2">
            <LabeledValue label="Approval Decision Behavior" value="" />
            <div className="space-x-3 flex items-center">
              <SwitchFormik id="predefined_notification_conditions.suppress_approval_decision" label="" />
              <div className="flex flex-col">
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM}>
                  Suppress approval decision notifications
                </Text>
                <Text variant={Text.VARIANTS.XS} className="text-gray-600">
                  When enabled, users will not receive notifications when their payment or reserve requests are approved
                  as-is.
                </Text>
                <Text variant={Text.VARIANTS.XS} className="text-gray-600">
                  Notifications for approvals with different amounts or rejections will still be sent.
                </Text>
              </div>
            </div>
          </div>
        ) : null}

        {notificationKey === 'FINANCIAL_REQUEST_GENERIC' ? (
          <div className="flex flex-col space-y-2">
            <LabeledValue label="Transaction Request Behavior" value="" />
            <div className="space-x-3 flex items-center">
              <SwitchFormik id="predefined_notification_conditions.suppress_transaction_request" label="" />
              <div className="flex flex-col">
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM}>
                  Suppress transaction request notifications
                </Text>
                <Text variant={Text.VARIANTS.XS} className="text-gray-600">
                  When enabled, users will not receive notifications when they are requested to make a decision on a
                  payment or reserve transaction.
                </Text>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
};

export default NotificationsConfigConditionsCard;
